import { z } from 'zod';
import { InstantSchema, JobOrderNoSchema } from './common.schema';

/**
 * What Accounts are shown of a case edited after intake, and the review of it.
 * The request of 5 October 2026 (third), section 3: each change with who made it
 * and when, and every field it changed with what the field read before and after;
 * a review refused when the case was edited again after the screen read it; and a
 * review taken back. The words and the keys are the domain's, in
 * `src/domain/order/accountsReview.ts`, the same as the server's AccountsReview.
 */

/**
 * One field as an edit changed it. `field` is one of the keys in the domain's
 * CHANGE_FIELDS (doctor, patient, origin, input, item, shade, instructions,
 * translucency, anatomy, incisal, occlusal, ingot, block, stain, itemsReceived,
 * teeth, units, span, value), kept a plain string so a key added later does not
 * fail an installed build. `before` is null on a field filled in for the first
 * time and `after` is null on one emptied; both are in the words the screen
 * prints. `hidden` is a doctor's or patient's name for a caller without
 * seeIdentity: the entry stays and both values are null.
 */
export const ChangedFieldSchema = z.object({
  field: z.string().min(1),
  before: z.string().nullable(),
  after: z.string().nullable(),
  hidden: z.boolean(),
});

/**
 * One edit: who made it and when, the sentence the Changed list prints for it,
 * and the fields it changed in the domain's order. A change that was waiting
 * from before old values were recorded has no id and no fields.
 */
export const ChangeSetSchema = z.object({
  id: z.string().nullable(),
  at: InstantSchema,
  byName: z.string().nullable(),
  summary: z.string(),
  fields: z.array(ChangedFieldSchema),
});

/** The review that stands on a case: when Accounts marked its changes reviewed, and who did. */
export const AccountsReviewedSchema = z.object({
  at: InstantSchema,
  byName: z.string(),
});

/**
 * `GET /job-orders/{jo}/accounts-review`. `waiting` is the changes still to be
 * reviewed and `reviewed.sets` the ones the standing review covered, both oldest
 * first. `since` is the order's `accountsReview.since`, null when nothing waits,
 * and is what Mark as reviewed sends back as `seen`.
 */
export const AccountsReviewDetailSchema = z.object({
  jobOrderNo: JobOrderNoSchema,
  since: InstantSchema.nullable(),
  waiting: z.array(ChangeSetSchema),
  reviewed: AccountsReviewedSchema.extend({ sets: z.array(ChangeSetSchema) }).nullable(),
});

/**
 * `POST /job-orders/{jo}/accounts-review`. The body is optional. A screen that
 * showed the change sends `seen`, the `since` it read, and is refused with 409
 * when the case was edited again after that. A build from before this request
 * sends no body and is answered as it always was.
 */
export const MarkReviewedSchema = z.object({
  seen: InstantSchema.optional(),
});

/**
 * `GET /accounts/review-cleanup`. The cases waiting for Accounts whose changes
 * are none of the bill's, under the rule of the request of 7 October 2026,
 * section 5: sent for review when every edit was, and not sent now. `pending`
 * is every case that waits and `needNoReview` how many of them are listed.
 */
export const ReviewCleanupSchema = z.object({
  pending: z.number().int().nonnegative(),
  needNoReview: z.number().int().nonnegative(),
  items: z.array(
    z.object({
      jobOrderNo: JobOrderNoSchema,
      since: InstantSchema.nullable(),
      summary: z.string(),
    }),
  ),
});

/** `POST /accounts/review-cleanup`. How many cases were cleared. */
export const ReviewCleanupResultSchema = z.object({
  cleared: z.number().int().nonnegative(),
});
